<template>
    <button
        role="tab"
        :aria-selected="isActive"
        class="group relative w-full text-left flex flex-col"
        :class="{ 'cursor-not-allowed': state === 'locked', 'cursor-pointer': state !== 'locked' }"
        :disabled="state === 'locked'"
        @click="handleClick">
        <div class="flex flex-col grow items-center text-center gap-2 py-3 sm:flex-row sm:items-center sm:text-left sm:gap-3">
            <!-- Icon -->
            <div
                class="shrink-0 rounded-full flex items-center justify-center transition-colors h-7 w-7"
                :class="[
                    state === 'completed' || state === 'open' ? 'bg-status-success-soft text-ink' : 'bg-surface-sunken text-ink-muted',
                    { 'opacity-40': isDimmed, 'group-hover:opacity-60': isDimmed },
                ]">
                <Checkmark v-if="state === 'completed'" width="14" height="14" />
                <Clock v-else-if="state === 'open'" width="14" height="14" />
                <Archive v-else-if="state === 'expired'" width="14" height="14" />
                <LockSmall v-else width="14" height="14" />
            </div>

            <div class="min-w-0 w-full sm:w-auto grow flex flex-col justify-between self-stretch">
                <!-- Moment title text -->
                <div class="details sm:body-small-bold line-clamp-2 sm:line-clamp-1" :class="isDimmed ? 'text-ink-muted' : 'text-ink'">
                    {{ isPreview ? t("components.moments.titlePreview", { title: momentTitle }) : momentTitle }}
                </div>
                <!-- Date and state text -->
                <!-- FIXME: one off font weight change -->
                <div class="details text-ink-muted font-medium mt-1 line-clamp-2 sm:line-clamp-1">
                    <span class="inline-block sm:inline">{{ date ? `${date}` : "" }}</span>
                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: separator between date and state -->
                    <span class="hidden sm:inline"> · </span>
                    <span class="font-normal sm:font-medium mt-1 sm:mt-0 block sm:inline whitespace-nowrap">{{ stateDisplay }}</span>
                </div>
            </div>
        </div>

        <div
            class="absolute -bottom-0.5 left-0 right-0 h-1 rounded-full"
            :class="[isActive ? 'bg-tabs-indicator' : 'bg-tabs-hairline', { 'opacity-40': isDimmed, 'group-hover:opacity-60': isDimmed }]"></div>
    </button>
</template>

<script setup lang="ts">
import Archive from "~vue/icons/Archive.vue";
import Checkmark from "~vue/icons/Checkmark.vue";
import Clock from "~vue/icons/Clock.vue";
import LockSmall from "~vue/icons/LockSmall.vue";
import type { PerformanceMomentState } from "~vue/types/performance_management";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
    state: PerformanceMomentState;
    date?: string;
    isActive?: boolean;
    momentId: number;
    momentTitle: string;
    isPreview?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    date: "",
    isActive: false,
});

const emit = defineEmits<{
    activate: [momentId: number];
}>();

// In-progress ("open") moments must never look greyed out, even when another tab is
// selected — orgs can run multiple active moments at once (ENG-1330). Only inactive
// states (locked/completed/expired) are dimmed when unselected.
const isDimmed = computed(() => !props.isActive && props.state !== "open");

const handleClick = () => {
    // Don't allow activating locked moments
    if (props.state === "locked") {
        return;
    }

    emit("activate", props.momentId);
};

const stateDisplay = computed(() => {
    switch (props.state) {
        case "locked":
            return t("components.moments.state.closed");
        case "open":
            return t("components.moments.state.inProgress");
        case "completed":
            return t("components.moments.state.completed");
        case "expired":
            return t("components.moments.state.expired");
        default:
            return "";
    }
});
</script>
